import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  IconButton,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import { ArrowBack, NavigateNext, Sync } from '@mui/icons-material';
import { useGetCampaignsQuery, useGetCampaignContactsQuery, useGetCampaignCallLogsQuery, useSyncCampaignCallDetailsMutation } from '../store/api/outboundCallingApi';
import { useGetAgentsForAccountQuery } from '../store/api/synthflowAgentsApi';
import { useGetActiveSynthflowAccountsQuery } from '../store/api/synthflowApi';
import CampaignStatus from '../components/outbound-calling/CampaignStatus';
import ContactStatusTab from '../components/outbound-calling/ContactStatusTab';
import CallLogsTab from '../components/outbound-calling/CallLogsTab';

const CampaignDetailsPage: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignIdNumber = campaignId ? parseInt(campaignId, 10) : null;
  
  // Get tab from URL parameter, default to 0 (Campaign Summary)
  const tabFromUrl = searchParams.get('tab');
  const initialTab = tabFromUrl ? parseInt(tabFromUrl, 10) : 0;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  // Get campaigns to find the specific campaign
  const { data: campaigns, isLoading, error } = useGetCampaignsQuery({});

  const campaign = campaigns?.find(c => c.id === campaignIdNumber);
  const selectedAgent = campaign?.agent_id;
  const selectedAccount = campaign?.umbrella_account_id || 1;

  // Get accounts and agents for breadcrumb
  const { data: accounts = [] } = useGetActiveSynthflowAccountsQuery();
  const { data: agents } = useGetAgentsForAccountQuery(selectedAccount, {
    skip: !selectedAccount
  });

  // Get campaign contacts and call logs
  const { data: contacts = [], isLoading: contactsLoading } = useGetCampaignContactsQuery(campaignIdNumber!, {
    skip: !campaignIdNumber
  });
  const { data: callLogs = [], isLoading: callLogsLoading } = useGetCampaignCallLogsQuery(campaignIdNumber!, {
    skip: !campaignIdNumber
  });

  // Sync mutation
  const [syncCallDetails, { isLoading: isSyncing }] = useSyncCampaignCallDetailsMutation();

  // Update activeTab when URL changes
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    const newTab = tabFromUrl ? parseInt(tabFromUrl, 10) : 0;
    setActiveTab(newTab);
  }, [searchParams]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Update URL with new tab parameter
    setSearchParams({ tab: newValue.toString() });
  };

  const handleSyncCallDetails = async () => {
    if (!campaignIdNumber) return;
    
    try {
      setSyncError(null);
      setSyncMessage(null);
      
      const result = await syncCallDetails(campaignIdNumber).unwrap();
      
      setSyncMessage(
        `Sync completed! Synced ${result.synced_count} call logs, ` +
        `updated ${result.updated_contacts} contacts. ` +
        `Failed: ${result.failed_count}`
      );
      
      // Clear message after 5 seconds
      setTimeout(() => setSyncMessage(null), 5000);
      
    } catch (error: any) {
      setSyncError(error.data?.detail || error.message || 'Failed to sync call details');
      // Clear error after 5 seconds
      setTimeout(() => setSyncError(null), 5000);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !campaign) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          Campaign not found or failed to load.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      {/* Navigation Breadcrumbs */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton 
            onClick={() => {
              const params = new URLSearchParams();
              if (selectedAgent) params.set('agent_id', selectedAgent.toString());
              if (selectedAccount) params.set('account_id', selectedAccount.toString());
              navigate(`/outbound-calling?${params.toString()}`);
            }} 
            sx={{ mr: 1 }}
          >
            <ArrowBack />
          </IconButton>
          <Breadcrumbs aria-label="breadcrumb" separator={<NavigateNext fontSize="small" />}>
            <Link color="inherit" onClick={() => navigate('/aigent/agents')} sx={{ cursor: 'pointer' }}>
              {accounts.find((acc: any) => acc.id === selectedAccount)?.name || 'Account'}
            </Link>
            {selectedAgent && agents && (
              <Link 
                color="inherit" 
                onClick={() => navigate('/aigent/agents')} 
                sx={{ cursor: 'pointer' }}
              >
                {agents.find((agent: any) => agent.id === selectedAgent)?.name || `Agent ${selectedAgent}`}
              </Link>
            )}
            <Link 
              color="inherit" 
              onClick={() => {
                const params = new URLSearchParams();
                if (selectedAgent) params.set('agent_id', selectedAgent.toString());
                if (selectedAccount) params.set('account_id', selectedAccount.toString());
                navigate(`/outbound-calling?${params.toString()}`);
              }}
              sx={{ cursor: 'pointer' }}
            >
              Campaigns
            </Link>
            <Typography color="text.primary">
              {campaign.name}
            </Typography>
          </Breadcrumbs>
        </Box>
      </Box>

      {/* Campaign Details */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Campaign Details
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<Sync />}
            onClick={handleSyncCallDetails}
            disabled={isSyncing || !campaignIdNumber}
            color="primary"
          >
            {isSyncing ? 'Syncing...' : 'Sync Call Details'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => {
              const params = new URLSearchParams();
              if (selectedAgent) params.set('agent_id', selectedAgent.toString());
              if (selectedAccount) params.set('account_id', selectedAccount.toString());
              navigate(`/outbound-calling?${params.toString()}`);
            }}
          >
            Back to Campaigns
          </Button>
        </Box>
      </Box>

      {/* Sync Messages */}
      {syncMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSyncMessage(null)}>
          {syncMessage}
        </Alert>
      )}
      {syncError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSyncError(null)}>
          {syncError}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="campaign details tabs">
          <Tab label="Campaign Summary" />
          <Tab label="Contact Status" />
          <Tab label="Call Logs" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && <CampaignStatus campaign={campaign} />}
      {activeTab === 1 && (
        <ContactStatusTab 
          contacts={contacts} 
          isLoading={contactsLoading} 
          campaignId={campaignIdNumber!}
          callLogs={callLogs}
          campaignStatus={campaign?.status}
          maxRetries={campaign?.max_retries}
        />
      )}
      {activeTab === 2 && (
        <CallLogsTab 
          callLogs={callLogs}
          campaignId={campaignIdNumber!} 
          isLoading={callLogsLoading}
          contacts={contacts}
        />
      )}
    </Container>
  );
};

export default CampaignDetailsPage;

